// Angular imports
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  OnChanges,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

// RxJS imports
import { Subject, takeUntil } from 'rxjs';

// Project libraries
import { UserData, UserProfileData, UserProfileUpdate } from '@tgx/shared/interfaces';
import { FormValidator } from '@tgx/shared/utils';
import { ToastsService } from '@tgx/shared/toasts';

// Local imports
import { OnboardingService } from '../../services/onboarding.service';
import { CompanyContactsInfo, ContactInfoData } from '../../interface/user-profile-contacts-info';
import {
  ContactStepData,
  ContactStepPrefix,
  ContactStepUserInfo,
  PermissionsUpdate,
} from '../../interface/company-contacts-step-data';
import { Steps } from '../../enum/steps';
import { StepInfo } from '../../interface/steps-info';
import { MembersRole, PermissionsService } from '@tgx/shared/enums';
import { ContactInfo } from '../../enum/contact-info';

@Component({
  selector: 'tgx-onboarding-company-company-contacts',
  templateUrl: './onboarding-company-company-contacts.component.html',
  styleUrls: ['./onboarding-company-company-contacts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingCompanyCompanyContactsComponent implements OnInit, OnDestroy, OnChanges {
  private fb = inject(FormBuilder);
  private toast = inject(ToastsService);
  private onboardingService = inject(OnboardingService);

  @Output() changeStep = new EventEmitter<Steps>();
  @Output() updateUserProfile = new EventEmitter<UserProfileUpdate>();
  @Input() isBuyer: boolean;
  @Input() isSeller: boolean;
  @Input() loggedUser: UserData;
  @Input() private selectedOrgCode: string;
  @Input() private orgMembersProfiles: UserProfileData[] = [];

  showMessageSendRegistration: Record<string, boolean> = {
    emailApi: false,
    emailTechDecision: false,
    emailTechSupport: false,
    emailBilling: false,
  };
  showMessageOwnRegistration: Record<string, boolean> = {
    emailApi: false,
    emailTechDecision: false,
    emailTechSupport: false,
    emailBilling: false,
  };
  showMessageDuplicatedFormData: Record<string, boolean> = {
    emailApi: false,
    emailTechDecision: false,
    emailTechSupport: false,
    emailBilling: false,
  };
  backupEmailContact: Record<string, string> = {
    emailApi: '',
    emailTechDecision: '',
    emailTechSupport: '',
    emailBilling: '',
  };
  private disabledInput: Record<string, boolean> = {
    firstNameApi: false,
    lastNameApi: false,
    emailApi: false,
    firstNameTechDecision: false,
    lastNameTechDecision: false,
    emailTechDecision: false,
    firstNameTechSupport: false,
    lastNameTechSupport: false,
    emailTechSupport: false,
    firstNameBilling: false,
    lastNameBilling: false,
    emailBilling: false,
  };
  companyContactForm: FormGroup;
  private destroy$ = new Subject<void>();
  private sentStepSnapshots = new Map<ContactStepPrefix, string>();
  protected initialPermissions: PermissionsUpdate = {};
  protected effectiveInitialPermissions: PermissionsUpdate = {};
  protected permissionsData: PermissionsUpdate = {};
  protected canEditCurrentStepRoles = true;
  protected isPermissionsFormInvalid = true;
  protected options: { label: string; value: string }[] = [];
  protected selectionModeByStep: Record<ContactStepPrefix, 'new' | 'existing'> = {
    Api: 'new',
    TechDecision: 'new',
    TechSupport: 'new',
    Billing: 'new',
  };
  private readonly ownerVisualPermissions: PermissionsUpdate = {
    [PermissionsService.HOTELX]: MembersRole.ADMIN,
    [PermissionsService.ENTITIES]: MembersRole.ADMIN,
    [PermissionsService.BILLING]: MembersRole.ADMIN,
    [PermissionsService.USER]: MembersRole.ADMIN,
    [PermissionsService.FASTX]: MembersRole.ADMIN,
  };
  protected contactStepData = new Map<ContactStepPrefix, ContactStepData>();

  contactTypesForm: { key: ContactInfo; prefix: ContactStepPrefix }[] = [
    { key: ContactInfo.API_IMPLEMENTATION, prefix: 'Api' },
    { key: ContactInfo.TECH_DECISION, prefix: 'TechDecision' },
    { key: ContactInfo.TECH_SUPPORT, prefix: 'TechSupport' },
    { key: ContactInfo.BILLING, prefix: 'Billing' },
  ];
  contactTypesFormPrefix: ContactStepPrefix[] = ['Api', 'TechDecision', 'TechSupport', 'Billing'];

  buttonName = 'Save & continue';

  isLoading = false;
  loggedUserProfile: ContactInfoData;
  isEditing = false;
  isInitialValue = true;

  // An organization can only be onboarded as buyer or seller (never both simultaneously),
  // show at template subtitle for buyer or seller org
  readonly BUYER_TECH_DECISION_MAKER_SUBTITLE = 'Notified of Travelgate Demand API changes affecting booking flow';
  readonly SELLER_TECH_DECISION_MAKER_SUBTITLE = "Authorises changes to Travelgate's development of partner's API";

  protected get userInformationSection() {
    return [
      {
        title: 'Commercial Decision Maker',
        subtitle: ' Contact for managing the development and integration process with Travelgate',
      },
      {
        title: 'Technical Decision Maker',
        subtitle: this.isBuyer ? this.BUYER_TECH_DECISION_MAKER_SUBTITLE : this.SELLER_TECH_DECISION_MAKER_SUBTITLE,
      },
      {
        title: 'Tech Support Contact',
        subtitle: 'Handles day-to-day technical operations & gets notified of any new connection activation requests',
      },
      {
        title: 'Billing Contact',
        subtitle: 'Contact for invoicing and billing matters',
      },
    ];
  }
  // Stepper
  protected steps = new Map<ContactInfo, StepInfo<ContactInfo>>([
    [
      ContactInfo.API_IMPLEMENTATION,
      { actualStep: true, name: ContactInfo.API_IMPLEMENTATION, width: 18, circle: '--active' },
    ],
    [ContactInfo.TECH_DECISION, { actualStep: false, name: ContactInfo.TECH_DECISION, width: 48, circle: '' }],
    [ContactInfo.TECH_SUPPORT, { actualStep: false, name: ContactInfo.TECH_SUPPORT, width: 78, circle: '' }],
    [ContactInfo.BILLING, { actualStep: false, name: ContactInfo.BILLING, width: 100, circle: '' }],
  ]);

  ngOnInit(): void {
    this.onboardingService.scrollTop();
    this.buildCompanyContactForm();
    this.initializeContactStepData();
    this.initializeStepSelectionState();
    this.updateValidatorsForCurrentStep();

    this.isEditing = this.onboardingService.getIsEditState();

    this.setupFormCompanyContacts('emailApi', 'firstNameApi', 'lastNameApi');
    this.setupFormCompanyContacts('emailBilling', 'firstNameBilling', 'lastNameBilling');
    this.setupFormCompanyContacts('emailTechDecision', 'firstNameTechDecision', 'lastNameTechDecision');
    this.setupFormCompanyContacts('emailTechSupport', 'firstNameTechSupport', 'lastNameTechSupport');

    this.refreshLoggedUserProfile();
    this.initializeMemberOptions();
    this.subscribeToSelectionModeChanges();
    this.initializeContacts();

    this.companyContactForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncContactStepUserInfo();
      this.syncPermissionsEditabilityState();

      if (this.isEditing) {
        this.refreshEditInitialState();
      }
      this.checkForInconsistencies();
    });

    // Scroll to specific contact type if editing from review
    const selectedContactType = this.onboardingService.getSelectedContactTypeToEdit();
    if (selectedContactType) {
      setTimeout(() => {
        this.scrollToContactSection(selectedContactType);
      }, 100);
      this.onboardingService.setSelectedContactTypeToEdit(null);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loggedUser'] || changes['orgMembersProfiles'] || changes['selectedOrgCode']) {
      this.refreshLoggedUserProfile();
      this.syncPermissionsEditabilityState();
      this.tryAutofillCurrentStepEmails();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private scrollToContactSection(contactType: ContactStepPrefix): void {
    const contactTypeMap: Record<ContactStepPrefix, number> = {
      'Api': 0,
      'TechDecision': 1,
      'TechSupport': 2,
      'Billing': 3,
    };

    const stepIndex = contactTypeMap[contactType];
    if (stepIndex !== undefined) {
      const stepsArray = Array.from(this.steps.entries());
      if (stepIndex >= 0 && stepIndex < stepsArray.length) {
        // Update the steps Map to set the target step as active
        const updatedSteps = new Map(
          stepsArray.map(([key, step], index) => [
            key,
            {
              ...step,
              actualStep: index === stepIndex,
              circle: index === stepIndex ? '--active' : index < stepIndex ? '--active' : '',
            },
          ]),
        );
        this.steps = updatedSteps;
        this.updateValidatorsForCurrentStep();
        this.loadStepDataFromMap();
        this.syncPermissionsForCurrentStep();
      }
    }
  }

  protected isButtonDisabled(): boolean {
    const isFormValid = this.companyContactForm.valid;
    const isPermissionsInvalid = this.isPermissionsFormInvalid;

    if (this.isEditing) {
      return !isFormValid || isPermissionsInvalid || this.isInitialValue || this.isLoading;
    }

    return !isFormValid || isPermissionsInvalid || this.isLoading;
  }

  private hasCurrentStepChanges(): boolean {
    const currentPrefix = this.getCurrentStepPrefix();
    const currentSnapshot = this.buildStepSnapshot(currentPrefix);
    const sentSnapshot = this.sentStepSnapshots.get(currentPrefix);

    // While initial data is still loading, keep the edit action disabled.
    if (sentSnapshot === undefined) {
      return false;
    }

    return sentSnapshot !== currentSnapshot;
  }

  private refreshEditInitialState(): void {
    this.isInitialValue = !this.hasCurrentStepChanges();
  }

  protected get currentContactStep(): number {
    const stepsList = Array.from(this.steps.values());
    const currentIndex = stepsList.findIndex((step) => step.actualStep);

    return currentIndex >= 0 ? currentIndex + 1 : 1;
  }

  protected get currentStepPrefix(): ContactStepPrefix {
    return this.getCurrentStepPrefix();
  }

  private isSelfOwnerByEmail(email: unknown): boolean {
    // In onboarding, self-owner means the logged OWNER is assigning permissions to their own contact.
    const normalizedEmail = this.normalizeEmail(email);
    const loggedEmail = this.normalizeEmail(this.loggedUser?.email);
    const loggedRole = String(this.loggedUser?.role ?? '').toUpperCase();

    return !!normalizedEmail && normalizedEmail === loggedEmail && loggedRole === MembersRole.OWNER;
  }

  private isCurrentStepSelfOwner(): boolean {
    const currentPrefix = this.getCurrentStepPrefix();
    const currentEmailFromForm = this.normalizeEmail(this.companyContactForm?.get(`email${currentPrefix}`)?.value);
    const currentSelectedUserEmail = this.normalizeEmail(
      this.companyContactForm?.get(`selectedUser${currentPrefix}`)?.value,
    );
    const currentStepData = this.contactStepData.get(currentPrefix);
    const stepEmail = this.normalizeEmail(currentStepData?.userInfo?.email);
    const currentEmail = currentEmailFromForm || currentSelectedUserEmail || stepEmail;

    return this.isSelfOwnerByEmail(currentEmail);
  }

  private syncPermissionsEditabilityState(): void {
    const isSelfOwner = this.isCurrentStepSelfOwner();

    // UI rule: self-owner permissions are read-only in onboarding.
    this.canEditCurrentStepRoles = !isSelfOwner;

    // For self-owner we display ADMIN labels in the selector, but this is visual-only.
    // Save flow intentionally omits owner permissions to preserve backend OWNER role.
    if (isSelfOwner) {
      this.effectiveInitialPermissions = this.ownerVisualPermissions;
      return;
    }

    if (this.effectiveInitialPermissions !== this.initialPermissions) {
      this.effectiveInitialPermissions = this.initialPermissions;
    }
  }

  private normalizeEmail(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  private refreshLoggedUserProfile(): void {
    const normalizedLoggedEmail = this.normalizeEmail(this.loggedUser?.email);
    const loggedUserOrgProfile = this.orgMembersProfiles.find(
      (profile) => this.normalizeEmail(profile?.email) === normalizedLoggedEmail,
    );
    const loggedUserPartnerInSelectedOrg = loggedUserOrgProfile?.partnersIncludedIn?.find(
      (partner) => partner.organization?.code === this.selectedOrgCode,
    );

    this.loggedUserProfile = {
      firstName: this.loggedUser?.firstName ?? '',
      lastName: this.loggedUser?.lastName ?? '',
      email: this.loggedUser?.email ?? '',
      jobTitle: loggedUserPartnerInSelectedOrg?.jobTitle ?? '',
    };
  }

  private tryAutofillCurrentStepEmails(): void {
    if (!this.companyContactForm) {
      return;
    }

    this.contactTypesFormPrefix.forEach((prefix) => {
      const emailControlName = `email${prefix}`;
      const firstNameControlName = `firstName${prefix}`;
      const lastNameControlName = `lastName${prefix}`;
      const email = this.companyContactForm.get(emailControlName)?.value;

      if (email) {
        this.validateAndAutofillFields(emailControlName, firstNameControlName, lastNameControlName, email);
      }
    });
  }

  // setup form controls for company contacts
  private setupFormCompanyContacts(
    emailControlName: string,
    firstNameControlName: string,
    lastNameControlName: string,
  ) {
    this.companyContactForm.controls[emailControlName].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        this.resetMessageStates(emailControlName);
        this.validateAndAutofillFields(emailControlName, firstNameControlName, lastNameControlName, email);
      });
  }

  // reset state of messages
  private resetMessageStates(emailControlInput: string): void {
    if (this.showMessageSendRegistration[emailControlInput]) {
      this.showMessageSendRegistration[emailControlInput] = false;
    }
    if (this.showMessageOwnRegistration[emailControlInput]) {
      this.showMessageOwnRegistration[emailControlInput] = false;
    }
  }

  // autofill fields with related loggedUser email data
  private validateAndAutofillFields(
    emailControlInput: string,
    firstNameControlInput: string,
    lastNameControlInput: string,
    email: string,
  ): void {
    if (!email) {
      this.clearInputRegisteredContact(emailControlInput, firstNameControlInput, lastNameControlInput);
    }
    this.backupEmailContact[emailControlInput] = email;

    if (this.companyContactForm.get(emailControlInput).valid) {
      const isLoggedUser = this.normalizeEmail(this.loggedUserProfile.email) === this.normalizeEmail(email);
      const firstNameValue = this.companyContactForm.get(firstNameControlInput)?.value?.trim();
      const lastNameValue = this.companyContactForm.get(lastNameControlInput)?.value?.trim();
      const jobTitleControlInput = this.getJobTitleControlNameByEmailControl(emailControlInput);
      const jobTitleValue = this.companyContactForm.get(jobTitleControlInput)?.value?.trim();
      const shouldAutofillLoggedUser = !firstNameValue && !lastNameValue && !jobTitleValue;

      if (isLoggedUser && shouldAutofillLoggedUser) {
        this.autoFillFieldLoggedUser(
          emailControlInput,
          firstNameControlInput,
          lastNameControlInput,
          jobTitleControlInput,
        );
      }

      this.updateUIStatesForEmail(emailControlInput, firstNameControlInput, lastNameControlInput, isLoggedUser);
    }
  }

  private checkForInconsistencies(): void {
    Object.keys(this.showMessageDuplicatedFormData).forEach((key) => {
      this.showMessageDuplicatedFormData[key] = false;
    });

    const formValues = this.contactTypesForm.map(({ prefix }) => ({
      email: this.companyContactForm.get(`email${prefix}`).value?.trim(),
      firstName: this.companyContactForm.get(`firstName${prefix}`).value?.trim(),
      lastName: this.companyContactForm.get(`lastName${prefix}`).value?.trim(),
      prefix,
    }));

    formValues.forEach((currentField, currentIndex) => {
      if (!currentField.email || !currentField.firstName || !currentField.lastName) {
        return;
      }

      formValues.forEach((field, index) => {
        // skip self comparation
        if (currentIndex === index) {
          return;
        }

        if (
          currentField.email === field.email &&
          currentField.firstName &&
          field.firstName &&
          currentField.lastName &&
          field.lastName &&
          (currentField.firstName !== field.firstName || currentField.lastName !== field.lastName)
        ) {
          // show error message for both fields
          this.showMessageDuplicatedFormData[`email${currentField.prefix}`] = true;
          this.showMessageDuplicatedFormData[`email${field.prefix}`] = true;
        }
      });
    });
  }

  private clearInputRegisteredContact(
    emailControlInput: string,
    firstNameControlInput: string,
    lastNameControlInput: string,
  ): void {
    const firstNameControl = this.companyContactForm.get(firstNameControlInput);
    const lastNameControl = this.companyContactForm.get(lastNameControlInput);

    if (
      this.normalizeEmail(this.loggedUserProfile.email) ===
      this.normalizeEmail(this.backupEmailContact[emailControlInput])
    ) {
      firstNameControl.setValue('');
      lastNameControl.setValue('');
      firstNameControl.markAsDirty();
      lastNameControl.markAsDirty();
    }
  }

  // autofill fields with logged user data
  private autoFillFieldLoggedUser(
    emailControlInput: string,
    firstNameControlInput: string,
    lastNameControlInput: string,
    jobTitleControlInput: string,
  ): void {
    this.companyContactForm.get(firstNameControlInput).setValue(this.loggedUserProfile.firstName);
    this.companyContactForm.get(firstNameControlInput).markAsDirty();
    this.companyContactForm.get(lastNameControlInput).setValue(this.loggedUserProfile.lastName);
    this.companyContactForm.get(lastNameControlInput).markAsDirty();
    this.companyContactForm.get(jobTitleControlInput).setValue(this.loggedUserProfile.jobTitle ?? '');
    this.companyContactForm.get(jobTitleControlInput).markAsDirty();
    this.companyContactForm.get(emailControlInput).markAsDirty();
  }

  private getJobTitleControlNameByEmailControl(emailControlInput: string): string {
    return emailControlInput.replace('email', 'jobTitle');
  }

  // update UI states for loggedUser and external email
  private updateUIStatesForEmail(
    emailControlInput: string,
    firstNameControlInput: string,
    lastNameControlInput: string,
    isLoggedUser: boolean,
  ): void {
    if (isLoggedUser) {
      this.showMessageOwnRegistration[emailControlInput] = true;
    } else {
      this.showMessageSendRegistration[emailControlInput] = true;
    }
  }

  private buildCompanyContactForm() {
    const formControls = {};

    this.contactTypesFormPrefix.forEach((prefix) => {
      formControls[`selectedUser${prefix}`] = this.fb.control({ value: '', disabled: true });
      formControls[`selectionMode${prefix}`] = this.fb.control('new');
      formControls[`firstName${prefix}`] = this.fb.control('', [
        FormValidator.textNumbers('first name'),
        FormValidator.textInvalidChar('first name'),
        FormValidator.maxLength('first name', 50),
      ]);
      formControls[`lastName${prefix}`] = this.fb.control('', [
        FormValidator.textNumbers('last name'),
        FormValidator.textInvalidChar('last name'),
        FormValidator.maxLength('last name', 50),
      ]);
      formControls[`email${prefix}`] = this.fb.control('', [FormValidator.emailRegex]);
      formControls[`jobTitle${prefix}`] = this.fb.control('', [
        FormValidator.textInvalidChar('job title'),
        FormValidator.maxLength('job title', 50),
      ]);
    });

    this.companyContactForm = this.fb.group(formControls);
  }

  private initializeStepSelectionState(): void {
    this.syncPermissionsForCurrentStep();
  }

  private initializeContactStepData(): void {
    this.contactTypesFormPrefix.forEach((prefix) => {
      this.contactStepData.set(prefix, {
        userInfo: this.getUserInfoByPrefix(prefix),
        userPermissions: {},
        isNewUser: true,
      });
    });
  }

  private syncContactStepUserInfo(): void {
    const currentPrefix = this.getCurrentStepPrefix();
    const currentStepData = this.contactStepData.get(currentPrefix);

    if (!currentStepData) {
      return;
    }

    this.contactStepData.set(currentPrefix, {
      ...currentStepData,
      userInfo: this.getUserInfoByPrefix(currentPrefix),
    });
  }

  private getUserInfoByPrefix(prefix: ContactStepPrefix): ContactStepUserInfo {
    return {
      firstName: this.companyContactForm.get(`firstName${prefix}`)?.value ?? '',
      lastName: this.companyContactForm.get(`lastName${prefix}`)?.value ?? '',
      jobTitle: this.companyContactForm.get(`jobTitle${prefix}`)?.value ?? '',
      email: this.companyContactForm.get(`email${prefix}`)?.value ?? '',
    };
  }

  private getCurrentStepPrefix(): ContactStepPrefix {
    return this.contactTypesFormPrefix[this.currentContactStep - 1];
  }

  protected getControlName(controlName: string, prefix: ContactStepPrefix = this.currentStepPrefix): string {
    return `${controlName}${prefix}`;
  }

  protected isDisabled(controlName: string): boolean {
    return this.disabledInput[controlName] ?? false;
  }

  protected getControlValue(controlName: string, prefix: ContactStepPrefix = this.currentStepPrefix): string {
    return this.companyContactForm.get(this.getControlName(controlName, prefix))?.value ?? '';
  }

  protected isExistingSelectionMode(prefix: ContactStepPrefix = this.currentStepPrefix): boolean {
    return this.selectionModeByStep[prefix] === 'existing';
  }

  protected get isCurrentStepUserFieldsDisabled(): boolean {
    if (this.selectionModeByStep[this.currentStepPrefix] === 'new') {
      return false;
    }
    return this.disabledInput[`firstName${this.currentStepPrefix}`] ?? false;
  }

  protected getInfoMessageState(
    stateGroup: 'showMessageSendRegistration' | 'showMessageOwnRegistration' | 'showMessageDuplicatedFormData',
    controlName: string,
  ): boolean {
    return this[stateGroup][controlName] ?? false;
  }

  private getSelectedUserControl(prefix: ContactStepPrefix): FormControl {
    return this.companyContactForm.get(`selectedUser${prefix}`) as FormControl;
  }

  private syncPermissionsForCurrentStep(): void {
    const currentStepData = this.contactStepData.get(this.getCurrentStepPrefix());
    const currentPermissions = currentStepData?.userPermissions ?? {};

    this.initialPermissions = currentPermissions;
    this.permissionsData = currentPermissions;
    this.syncPermissionsEditabilityState();
  }

  private loadStepDataFromMap(): void {
    const currentPrefix = this.getCurrentStepPrefix();
    const stepData = this.contactStepData.get(currentPrefix);

    if (!stepData) {
      return;
    }

    // Load all fields for current step from map
    const valuesToPatch = {
      [`firstName${currentPrefix}`]: stepData.userInfo.firstName,
      [`lastName${currentPrefix}`]: stepData.userInfo.lastName,
      [`jobTitle${currentPrefix}`]: stepData.userInfo.jobTitle,
      [`email${currentPrefix}`]: stepData.userInfo.email,
      [`selectionMode${currentPrefix}`]: stepData.isNewUser ? 'new' : 'existing',
      [`selectedUser${currentPrefix}`]: '', // Always clear selector on load
    };

    this.companyContactForm.patchValue(valuesToPatch, { emitEvent: false });

    // Update selector state based on selection mode
    this.handleSelectionModeChange(currentPrefix, stepData.isNewUser ? 'new' : 'existing');
  }

  private updateValidatorsForCurrentStep(): void {
    const currentPrefix = this.contactTypesFormPrefix[this.currentContactStep - 1];

    this.contactTypesFormPrefix.forEach((prefix) => {
      const isCurrentStep = prefix === currentPrefix;

      this.companyContactForm
        .get(`firstName${prefix}`)
        ?.setValidators([
          ...(isCurrentStep ? [FormValidator.required('first name')] : []),
          FormValidator.textNumbers('first name'),
          FormValidator.textInvalidChar('first name'),
          FormValidator.maxLength('first name', 50),
        ]);

      this.companyContactForm
        .get(`lastName${prefix}`)
        ?.setValidators([
          ...(isCurrentStep ? [FormValidator.required('last name')] : []),
          FormValidator.textNumbers('last name'),
          FormValidator.textInvalidChar('last name'),
          FormValidator.maxLength('last name', 50),
        ]);

      this.companyContactForm
        .get(`email${prefix}`)
        ?.setValidators([...(isCurrentStep ? [FormValidator.required('email')] : []), FormValidator.emailRegex]);

      this.companyContactForm
        .get(`jobTitle${prefix}`)
        ?.setValidators([
          ...(isCurrentStep ? [FormValidator.required('job title')] : []),
          FormValidator.textInvalidChar('job title'),
          FormValidator.maxLength('job title', 50),
        ]);

      this.companyContactForm.get(`firstName${prefix}`)?.updateValueAndValidity({ emitEvent: false });
      this.companyContactForm.get(`lastName${prefix}`)?.updateValueAndValidity({ emitEvent: false });
      this.companyContactForm.get(`email${prefix}`)?.updateValueAndValidity({ emitEvent: false });
      this.companyContactForm.get(`jobTitle${prefix}`)?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private initializeContacts() {
    this.onboardingService
      .getUserProfileContactInfo(this.loggedUser.email)
      .then((res) => {
        if (res) {
          const patchValues = {};

          this.contactTypesForm.forEach(({ key, prefix }) => {
            const contactInfo = res[key] ?? res[prefix];

            if (contactInfo?.firstName) patchValues[`firstName${prefix}`] = contactInfo.firstName;
            if (contactInfo?.lastName) patchValues[`lastName${prefix}`] = contactInfo.lastName;
            if (contactInfo?.email) patchValues[`email${prefix}`] = contactInfo.email;
            if (contactInfo?.jobTitle) patchValues[`jobTitle${prefix}`] = contactInfo.jobTitle;

            // Load permissions from API into contactStepData
            const stepData = this.contactStepData.get(prefix);
            if (stepData && contactInfo) {
              const userPermissions: PermissionsUpdate = {};
              if (contactInfo[PermissionsService.HOTELX])
                userPermissions[PermissionsService.HOTELX] = contactInfo[PermissionsService.HOTELX];
              if (contactInfo[PermissionsService.ENTITIES])
                userPermissions[PermissionsService.ENTITIES] = contactInfo[PermissionsService.ENTITIES];
              if (contactInfo[PermissionsService.FASTX])
                userPermissions[PermissionsService.FASTX] = contactInfo[PermissionsService.FASTX];
              if (contactInfo[PermissionsService.USER])
                userPermissions[PermissionsService.USER] = contactInfo[PermissionsService.USER];
              if (contactInfo[PermissionsService.BILLING])
                userPermissions[PermissionsService.BILLING] = contactInfo[PermissionsService.BILLING];

              this.contactStepData.set(prefix, {
                ...stepData,
                userInfo: {
                  firstName: contactInfo.firstName ?? '',
                  lastName: contactInfo.lastName ?? '',
                  email: contactInfo.email ?? '',
                  jobTitle: contactInfo.jobTitle ?? '',
                },
                userPermissions,
                isNewUser: !contactInfo.email,
              });
            }
          });

          this.companyContactForm.patchValue(patchValues, { emitEvent: false });

          Object.keys(this.companyContactForm.controls).forEach((field) => {
            const control = this.companyContactForm.get(field);
            if (control?.value) control?.markAsDirty();
          });

          // Sync permissions from loaded data to initialPermissions
          this.syncPermissionsForCurrentStep();

          // Initialise snapshots with loaded data so only real changes trigger API calls
          this.contactTypesFormPrefix.forEach((prefix) => {
            this.sentStepSnapshots.set(prefix, this.buildStepSnapshot(prefix));
          });

          if (this.isEditing) this.buttonName = 'Update';
          this.refreshEditInitialState();
        }
      })
      .catch((error) => {
        this.toast.addToast('Error retreiving contact', 'bg-danger', error);
      });
  }

  private initializeMemberOptions(): void {
    const memberEmails = Array.from(
      new Set(this.orgMembersProfiles.map((profile) => profile?.email).filter((email) => !!email)),
    );

    if (!memberEmails.length) {
      this.options = [];
      return;
    }

    this.options = memberEmails.map((email) => {
      const userProfile = this.orgMembersProfiles.find((profile) => profile.email === email);
      const fullName = [userProfile?.firstName, userProfile?.lastName]
        .filter((name) => !!name)
        .join(' ')
        .trim();

      return {
        label: fullName ? `${fullName} (${email})` : email,
        value: email,
      };
    });
  }

  private subscribeToSelectionModeChanges(): void {
    this.contactTypesFormPrefix.forEach((prefix) => {
      const selectionModeControl = this.companyContactForm.get(`selectionMode${prefix}`);

      if (!selectionModeControl) {
        return;
      }

      selectionModeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((mode: 'new' | 'existing') => {
        this.handleSelectionModeChange(prefix, mode);
      });
    });
  }

  protected handleMemberSelectionMode(isNewUser: boolean, step: number = this.currentContactStep): void {
    const prefix = this.contactTypesFormPrefix[step - 1];
    const mode = isNewUser ? 'new' : 'existing';
    const selectionModeControl = this.companyContactForm.get(`selectionMode${prefix}`);

    if (selectionModeControl) {
      selectionModeControl.setValue(mode, { emitEvent: false });
    }

    this.handleSelectionModeChange(prefix, mode);
  }

  private handleSelectionModeChange(prefix: ContactStepPrefix, mode: 'new' | 'existing'): void {
    this.selectionModeByStep[prefix] = mode;

    const currentStepData = this.contactStepData.get(prefix);
    if (currentStepData) {
      this.contactStepData.set(prefix, {
        ...currentStepData,
        isNewUser: mode === 'new',
      });
    }

    const selectedUserControl = this.getSelectedUserControl(prefix);

    if (mode === 'existing') {
      selectedUserControl.enable({ emitEvent: false });
      this.syncPermissionsEditabilityState();
      return;
    }

    selectedUserControl.setValue('', { emitEvent: false });
    selectedUserControl.disable({ emitEvent: false });
    this.syncPermissionsEditabilityState();
  }

  protected onSelectedUserChange(prefix: string, event?: Event | string): void {
    const stepPrefix = prefix as ContactStepPrefix;
    const selectedEmail = this.resolveSelectedEmail(stepPrefix, event);

    if (!selectedEmail) {
      return;
    }

    this.selectionModeByStep[stepPrefix] = 'existing';
    this.getSelectedUserControl(stepPrefix).setValue(selectedEmail, { emitEvent: false });
    this.handleSelectionModeChange(stepPrefix, 'existing');
    this.applyProfileToStep(prefix, selectedEmail);
  }

  private applyProfileToStep(prefix: string, selectedEmail: string): void {
    const selectedProfile = this.orgMembersProfiles.find((profile) => profile.email === selectedEmail);

    if (!selectedProfile) {
      return;
    }

    const partnerForOrg = selectedProfile.partnersIncludedIn?.find(
      (partner) => partner.organization?.code === this.selectedOrgCode,
    );

    this.companyContactForm.patchValue({
      [`firstName${prefix}`]: selectedProfile.firstName ?? '',
      [`lastName${prefix}`]: selectedProfile.lastName ?? '',
      [`email${prefix}`]: selectedProfile.email ?? '',
      [`jobTitle${prefix}`]: partnerForOrg?.jobTitle ?? '',
    });

    this.companyContactForm.get(`firstName${prefix}`)?.markAsDirty();
    this.companyContactForm.get(`lastName${prefix}`)?.markAsDirty();
    this.companyContactForm.get(`email${prefix}`)?.markAsDirty();
    this.companyContactForm.get(`jobTitle${prefix}`)?.markAsDirty();
  }

  private resolveSelectedEmail(prefix: ContactStepPrefix, event?: Event | string): string {
    if (typeof event === 'string') {
      return event;
    }

    const customEventValue = (event as CustomEvent<{ value?: string }>)?.detail?.value;
    if (customEventValue) {
      return customEventValue;
    }

    const valueFromObject = (event as { value?: string } | undefined)?.value;
    if (valueFromObject) {
      return valueFromObject;
    }

    const eventTarget = event?.target as HTMLSelectElement | null;
    if (eventTarget?.value) {
      return eventTarget.value;
    }

    return this.getSelectedUserControl(prefix).value;
  }

  getValidationStatus(controlName: string, isRequired: boolean): string {
    return this.onboardingService.getValidationStatus(this.companyContactForm, controlName, isRequired);
  }

  submitCompanyContacts(): void {
    // Validate current form before moving to next step
    if (!this.companyContactForm.valid) {
      return;
    }

    const stepsArray = Array.from(this.steps.entries());
    const isLastStep = this.currentContactStep === stepsArray.length;

    this.saveContactsToAPI().then((isSaved) => {
      if (!isSaved) {
        return;
      }

      if (!this.isEditing && !isLastStep) {
        this.advanceToNextStep();
      }
    });
  }

  private advanceToNextStep(): void {
    const stepsArray = Array.from(this.steps.entries());
    const currentIndex = stepsArray.findIndex(([, step]) => step.actualStep);

    if (currentIndex >= 0 && currentIndex < stepsArray.length - 1) {
      this.setActiveStep(currentIndex + 1);
    }
  }

  protected goToPreviousStep(): void {
    const stepsArray = Array.from(this.steps.entries());
    const currentIndex = stepsArray.findIndex(([, step]) => step.actualStep);

    if (currentIndex > 0) {
      this.setActiveStep(currentIndex - 1);
    }
  }

  private setActiveStep(targetIndex: number): void {
    const stepsArray = Array.from(this.steps.entries());

    this.steps = new Map(
      stepsArray.map(([key, step], index) => [
        key,
        {
          ...step,
          actualStep: index === targetIndex,
          circle: index === targetIndex ? '--active' : index < targetIndex ? '--active' : '',
        },
      ]),
    );

    this.updateValidatorsForCurrentStep();
    this.loadStepDataFromMap();
    this.syncPermissionsForCurrentStep();

    if (this.isEditing) {
      this.refreshEditInitialState();
    }
  }

  private buildStepSnapshot(prefix: ContactStepPrefix): string {
    const stepData = this.contactStepData.get(prefix);

    return JSON.stringify({
      userInfo: {
        email: stepData?.userInfo?.email ?? '',
        firstName: stepData?.userInfo?.firstName ?? '',
        lastName: stepData?.userInfo?.lastName ?? '',
        jobTitle: stepData?.userInfo?.jobTitle ?? '',
      },
      userPermissions: stepData?.userPermissions ?? {},
    });
  }

  private saveContactsToAPI(): Promise<boolean> {
    const currentStep = this.currentContactStep;
    const stepsArray = Array.from(this.steps.entries());
    const isLastStep = currentStep === stepsArray.length;
    const currentPrefix = this.getCurrentStepPrefix();
    const currentSnapshot = this.buildStepSnapshot(currentPrefix);
    const shouldSend = this.sentStepSnapshots.get(currentPrefix) !== currentSnapshot;

    if (!shouldSend) {
      if (!this.isEditing && isLastStep) {
        this.changeStep.emit(Steps.BILLING);
        this.finishCompanyContacts();
      }
      return Promise.resolve(true);
    }

    if (isLastStep) this.isLoading = true;

    const data = this.contactTypesForm.reduce((acc, { key, prefix }) => {
      const value = this.contactStepData.get(prefix);

      if (!value) {
        return acc;
      }

      const isSelfOwnerContact = this.isSelfOwnerByEmail(value.userInfo.email);

      const hasData = isSelfOwnerContact
        ? [value.userInfo.email, value.userInfo.firstName, value.userInfo.lastName, value.userInfo.jobTitle].some(
            (field) => !!field?.trim(),
          )
        : [
            value.userInfo.email,
            value.userInfo.firstName,
            value.userInfo.lastName,
            value.userInfo.jobTitle,
            value.userPermissions[PermissionsService.HOTELX],
            value.userPermissions[PermissionsService.ENTITIES],
            value.userPermissions[PermissionsService.FASTX],
            value.userPermissions[PermissionsService.USER],
            value.userPermissions[PermissionsService.BILLING],
          ].some((field) => !!field?.trim());

      if (!hasData) {
        return acc;
      }

      const baseContactData = {
        email: value.userInfo.email,
        firstName: value.userInfo.firstName,
        lastName: value.userInfo.lastName,
        jobTitle: value.userInfo.jobTitle,
      };

      if (isSelfOwnerContact) {
        // Preserve OWNER role in backend by never sending permissions for self-owner contact.
        acc[key] = baseContactData;
        return acc;
      }

      acc[key] = {
        ...baseContactData,
        [PermissionsService.HOTELX]: value.userPermissions[PermissionsService.HOTELX],
        [PermissionsService.ENTITIES]: value.userPermissions[PermissionsService.ENTITIES],
        [PermissionsService.FASTX]: value.userPermissions[PermissionsService.FASTX],
        [PermissionsService.USER]: value.userPermissions[PermissionsService.USER],
        [PermissionsService.BILLING]: value.userPermissions[PermissionsService.BILLING],
      };
      return acc;
    }, {} as CompanyContactsInfo);

    const persistPromise = this.isEditing
      ? this.onboardingService
          .getUserProfileContactInfo(this.loggedUser.email)
          .then((currentContacts) => {
            const mergedContacts: CompanyContactsInfo = {
              ...(currentContacts || {}),
              ...data,
            };

            return this.onboardingService.updateUserProfileContactInfo(this.loggedUser.email, mergedContacts);
          })
          .catch((error) => Promise.reject(error))
      : this.onboardingService.updateUserProfileContactInfo(this.loggedUser.email, data);

    return persistPromise
      .then(() => {
        this.sentStepSnapshots.set(currentPrefix, currentSnapshot);

        if (this.isEditing) {
          this.toast.addToast('Company contacts updated successfully', 'bg-success');
          this.changeStep.emit(Steps.REVIEW);
        } else if (isLastStep) {
          this.changeStep.emit(Steps.BILLING);
          this.finishCompanyContacts();
        }

        return true;
      })
      .catch((error) => {
        const detail = typeof error === 'string' ? error : "We couldn't save the contacts. Please try again.";
        this.toast.addToast('Error save contacts', 'bg-danger', detail);
        return false;
      })
      .finally(() => {
        if (isLastStep) {
          this.isLoading = false;
        }
      });
  }

  private finishCompanyContacts() {
    const date = new Date().toISOString();
    this.updateUserProfile.emit({ onboardingProperties: { companyContactsFinishedAt: date } });
  }

  protected cancelChanges() {
    this.changeStep.emit(Steps.REVIEW);
  }

  protected handlePermissionsChanged(changes: PermissionsUpdate): void {
    const currentStepPrefix = this.getCurrentStepPrefix();
    const currentStepData = this.contactStepData.get(currentStepPrefix);
    const nextPermissions = {
      ...(currentStepData?.userPermissions ?? {}),
      ...changes,
    };

    if (currentStepData) {
      this.contactStepData.set(currentStepPrefix, {
        ...currentStepData,
        userPermissions: nextPermissions,
      });
    }

    this.permissionsData = { ...nextPermissions };

    if (this.isEditing) {
      this.refreshEditInitialState();
    }
  }

  protected handleInvalidForm(isInvalid: boolean): void {
    this.isPermissionsFormInvalid = isInvalid;
  }
}
